import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { newHttpService } from '../../services/new-http-service'
import { utilService } from '../../services/util-service'
import { createEntityMutation } from '../../cmps/util-cmps/graphql-mutations/create-entity'
import { envService } from '../../services/env-service'
import { RootState } from '../../store/configure-store'

interface StepConfig {
    key: string
    label: string
    description: string
}

const STEPS: StepConfig[] = [
    { key: 'company-setup', label: 'Company Setup', description: 'Create company record in the database' },
    { key: 'domain-setup', label: 'Domain Setup', description: 'Create CNAME record for customer portal' },
    { key: 'infrastructure', label: 'AWS Setup', description: 'AWS provisioning, logo, and verification' },
    { key: 'entities-setup', label: 'Entities', description: 'Entity context domains and audit year' },
    { key: 'users', label: 'Users', description: 'Cognito users and admin team' },
    { key: 'review', label: 'Review', description: 'Verify all onboarding steps' },
]

const COMPANY_NAME_REGEX = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/

interface AdminUser {
    email: string
    firstName: string
    lastName: string
}

const DEFAULT_ADMIN_USERS: AdminUser[] = [
    { email: 'ronib@audit-tech.net', firstName: 'Roni', lastName: 'Beeri' },
    { email: 'eedry@audit-tech.net', firstName: 'Eli', lastName: 'Edry' },
    { email: 'yefid@audit-tech.net', firstName: 'Yefi', lastName: 'Dolev' },
    { email: 'ifatb@audit-tech.io', firstName: 'Ifat', lastName: 'Bergman' },
]

interface StepResult {
    success: boolean
    message: string
    detail?: string
    retryable?: boolean
    hint?: string
}

const DNS_SUFFIX = envService.dnsSuffix
const COMPANY_DOMAIN = envService.companyDomain
const IS_US = envService.isUS

export const OnboardingPage: React.FC = () => {
    const [activeStep, setActiveStep] = useState(0)
    const [companyName, setCompanyName] = useState('')

    // Step 0: Company Setup
    const [abbreviation, setAbbreviation] = useState('')
    const [isSilo, setIsSilo] = useState(false)
    const [customDisplayName, setCustomDisplayName] = useState('')
    const [useCustomName, setUseCustomName] = useState(false)
    const autoDisplayName = companyName.replace(/(^|-)(\w)/g, (_, sep, ch) => (sep ? ' ' : '') + ch.toUpperCase()).trim()
    const displayName = useCustomName ? customDisplayName : autoDisplayName
    const [isCompanySubmitting, setIsCompanySubmitting] = useState(false)
    const [companyResult, setCompanyResult] = useState<StepResult | null>(null)

    // Step 1: Infrastructure — DNS
    const [isDnsSubmitting, setIsDnsSubmitting] = useState(false)
    const [dnsResult, setDnsResult] = useState<StepResult | null>(null)

    // Step 1: Infrastructure — AWS Provision / Verify
    const [isProvisionSubmitting, setIsProvisionSubmitting] = useState(false)
    const [provisionResult, setProvisionResult] = useState<StepResult | null>(null)
    const [isVerifySubmitting, setIsVerifySubmitting] = useState(false)
    const [verifyResources, setVerifyResources] = useState<{ name: string; status: string }[] | null>(null)
    const [verifyError, setVerifyError] = useState<string | null>(null)

    // Step 1: Infrastructure — Logo
    const [logoFile, setLogoFile] = useState<File | null>(null)
    const [logoPreview, setLogoPreview] = useState<string | null>(null)
    const [isLogoSubmitting, setIsLogoSubmitting] = useState(false)
    const [logoResult, setLogoResult] = useState<StepResult | null>(null)
    const [isDragging, setIsDragging] = useState(false)
    const dragCounter = React.useRef(0)

    // Health Report (silo only, shown in Review step)
    const [healthReportChecked, setHealthReportChecked] = useState(false)

    // Step 3: Entities — Create Entities
    const [createdCompanyId, setCreatedCompanyId] = useState<number | null>(null)
    const [entityName, setEntityName] = useState('')
    const [entityAbbreviation, setEntityAbbreviation] = useState('')
    const [isEntitySubmitting, setIsEntitySubmitting] = useState(false)
    const [entityResult, setEntityResult] = useState<StepResult | null>(null)
    const [createdEntities, setCreatedEntities] = useState<{ id: number; name: string }[]>([])

    // Step 3: Entities — Context & Audit Year
    const [auditors, setAuditors] = useState<{ id: number; name: string }[]>([])
    const [selectedAuditorId, setSelectedAuditorId] = useState<number | null>(null)
    const [domainInput, setDomainInput] = useState('')
    const [isDomainSubmitting, setIsDomainSubmitting] = useState(false)
    const [domainResult, setDomainResult] = useState<StepResult | null>(null)
    const [isAuditYearSubmitting, setIsAuditYearSubmitting] = useState(false)
    const [auditYearResult, setAuditYearResult] = useState<StepResult | null>(null)

    // Step 4: Users — Cognito Users
    const [userEmail, setUserEmail] = useState('')
    const [userPassword, setUserPassword] = useState('')
    const generatePassword = () => {
        const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
        const lower = 'abcdefghjkmnpqrstuvwxyz'
        const digits = '23456789'
        const special = '!@#$%'
        const all = upper + lower + digits + special
        // Ensure at least one of each type
        const required = [
            upper[Math.floor(Math.random() * upper.length)],
            lower[Math.floor(Math.random() * lower.length)],
            digits[Math.floor(Math.random() * digits.length)],
            special[Math.floor(Math.random() * special.length)],
        ]
        for (let i = required.length; i < 12; i++) required.push(all[Math.floor(Math.random() * all.length)])
        // Shuffle
        for (let i = required.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [required[i], required[j]] = [required[j], required[i]] }
        return required.join('')
    }
    const [suggestedPassword, setSuggestedPassword] = useState(generatePassword)
    const [isUserSubmitting, setIsUserSubmitting] = useState(false)
    const [isCheckingUser, setIsCheckingUser] = useState(false)
    const [userCheckResult, setUserCheckResult] = useState<{ exists: boolean; in_group: boolean; status: string | null } | null>(null)
    const [userResult, setUserResult] = useState<StepResult | null>(null)
    const [createdUsers, setCreatedUsers] = useState<{ email: string; password: string }[]>([])

    // Step 4: Users — Existing users tables
    const [cognitoGroupUsers, setCognitoGroupUsers] = useState<{ email: string; status: string; enabled: boolean }[]>([])
    const [existingAdminUsers, setExistingAdminUsers] = useState<{ email: string; first_name: string; last_name: string; roles: string[] }[]>([])

    // Step 4: Users — AudiTech Platform Admin Users
    const [adminUsers, setAdminUsers] = useState<AdminUser[]>(DEFAULT_ADMIN_USERS.map(u => ({ ...u })))
    const [isAdminSubmitting, setIsAdminSubmitting] = useState(false)
    const [adminResult, setAdminResult] = useState<StepResult | null>(null)
    const [adminAuditSnippet, setAdminAuditSnippet] = useState<{ actor: string; time: string } | null>(null)
    const currentUser = useSelector((state: RootState) => state.users.user)

    const isValidName = companyName.length > 0 && COMPANY_NAME_REGEX.test(companyName)
    const isBucketReady = provisionResult?.success || (verifyResources && verifyResources.every(r => r.status === 'exists'))

    const actorLabel = (email: string | null) => {
        if (!email) return 'Current admin'
        const local = email.split('@')[0]
        return local.split(/[._-]/).map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    }

    const fetchUsersForCompany = useCallback(async () => {
        if (!isValidName) return
        try {
            const [cognitoRes, adminRes] = await Promise.all([
                newHttpService.get('/api/backoffice/onboarding/cognito-group-users', { company_name: companyName }).catch(() => ({ users: [] })),
                newHttpService.get('/api/backoffice/onboarding/admin-users', { company_name: companyName }).catch(() => ({ users: [] })),
            ])
            setCognitoGroupUsers(cognitoRes.users || [])
            setExistingAdminUsers(adminRes.users || [])
        } catch { /* ignore */ }
    }, [companyName, isValidName])

    // Fetch existing users when entering step 4
    useEffect(() => {
        if (activeStep === 4) fetchUsersForCompany()
    }, [activeStep, fetchUsersForCompany])

    // Fetch auditors from main DB (auditors are global)
    useEffect(() => {
        const fetchAuditors = async () => {
            try {
                const res = await newHttpService.graphql('auditech', `{ auditors { id name } }`)
                setAuditors(res.auditors || [])
            } catch { /* ignore */ }
        }
        fetchAuditors()
    }, [])

    // Auto-load existing onboarding state when company name changes (debounced)
    const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [isLoadingState, setIsLoadingState] = useState(false)

    const loadOnboardingState = useCallback(async (name: string) => {
        if (!name || !COMPANY_NAME_REGEX.test(name)) return
        setIsLoadingState(true)
        try {
            const query = `{
                companies(name: "${name}") {
                    id name abbreviation isSilo
                    entity(sorting: ASC) {
                        id name abbreviation
                        entityContext { id domains }
                        audit(sorting: DESC) { id year }
                        entitySystems(sorting: ASC) { id }
                    }
                    user { id email firstName lastName }
                }
            }`
            // Try the company's own DB first, fall back to main
            let res
            try {
                res = await newHttpService.graphql(name, query)
            } catch {
                res = await newHttpService.graphql('auditech', query)
            }
            const company = res?.companies?.[0]
            if (!company) {
                // Company doesn't exist yet — reset state
                setCreatedCompanyId(null)
                setCompanyResult(null)
                setCreatedEntities([])
                setDomainResult(null)
                setAuditYearResult(null)
                return
            }

            // Pre-populate company info
            setCreatedCompanyId(company.id)
            setAbbreviation(company.abbreviation || '')
            setIsSilo(!!company.isSilo)
            setCompanyResult({ success: true, message: `Company "${name}" found (ID: ${company.id})`, hint: 'auto-loaded' })

            // Pre-populate entities
            const entities = company.entity || []
            if (entities.length > 0) {
                setCreatedEntities(entities.map((e: any) => ({ id: e.id, name: e.name })))
            }

            // Check entity context status
            const allHaveContext = entities.length > 0 && entities.every((e: any) => !!e.entityContext)
            if (allHaveContext && entities.length > 0) {
                const domains = entities[0].entityContext?.domains
                if (domains) setDomainInput(Array.isArray(domains) ? domains.join(', ') : String(domains))
                setDomainResult({ success: true, message: 'Entity context already configured' })
            }

            // Check audit year status
            const currentYear = new Date().getFullYear()
            const entitiesMissingAudit = entities.filter((e: any) =>
                !(e.audit || []).some((a: any) => Number(a.year) === currentYear)
            )
            if (entities.length > 0 && entitiesMissingAudit.length === 0) {
                setAuditYearResult({ success: true, message: `Audit year ${currentYear} already exists` })
            } else if (entitiesMissingAudit.length > 0) {
                const missing = entitiesMissingAudit.map((e: any) => e.name).join(', ')
                setAuditYearResult({ success: false, message: `Audit year ${currentYear} missing for: ${missing}` })
            }

            // Auto-fetch existing Cognito group members and admin users
            try {
                const [cognitoRes, adminRes] = await Promise.all([
                    newHttpService.get('/api/backoffice/onboarding/cognito-group-users', { company_name: name }).catch(() => ({ users: [] })),
                    newHttpService.get('/api/backoffice/onboarding/admin-users', { company_name: name }).catch(() => ({ users: [] })),
                ])
                setCognitoGroupUsers(cognitoRes.users || [])
                setExistingAdminUsers(adminRes.users || [])
            } catch {
                // Failed to fetch users — that's fine
            }

            // Auto-check if domain is reachable (server-side)
            try {
                const checkData = await newHttpService.post('/api/backoffice/onboarding/check-domain', { company_name: name })
                if (checkData.reachable) {
                    setDnsResult({ success: true, message: `Domain is reachable: ${checkData.url}` })
                }
            } catch {
                // Check failed — leave DNS step as pending
            }

            // Auto-run verify to check AWS resources
            try {
                const verifyData = await newHttpService.post('/api/backoffice/onboarding/verify-provision', { company_name: name })
                if (verifyData.resources) {
                    setVerifyResources(verifyData.resources)
                    // If all AWS resources exist, mark provision as done
                    const awsOk = verifyData.resources
                        .filter((r: any) => r.name !== 'Company Logo')
                        .every((r: any) => r.status === 'exists')
                    if (awsOk) {
                        setProvisionResult({ success: true, message: 'AWS resources already provisioned' })
                    }
                    const logoOk = verifyData.resources.find((r: any) => r.name === 'Company Logo')?.status === 'exists'
                    if (logoOk) {
                        setLogoResult({ success: true, message: 'Logo already uploaded' })
                    }
                }
            } catch {
                // Verify failed — that's fine, user can run it manually
            }
        } catch {
            // Company not found in this DB — that's fine, it may not exist yet
        } finally {
            setIsLoadingState(false)
        }
    }, [])

    const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newName = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')
        setCompanyName(newName)
        setAbbreviation('')
        setDnsResult(null)
        setProvisionResult(null)
        setCompanyResult(null)
        setCreatedCompanyId(null)
        setCreatedEntities([])
        setCreatedUsers([])
        setDomainResult(null)
        setAuditYearResult(null)
        setAdminResult(null)
        setLogoResult(null)

        // Debounce: auto-load state 800ms after user stops typing
        if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current)
        if (newName && COMPANY_NAME_REGEX.test(newName)) {
            loadTimeoutRef.current = setTimeout(() => loadOnboardingState(newName), 800)
        }
    }

    const handleCreateDns = async () => {
        if (!isValidName || isDnsSubmitting) return
        setIsDnsSubmitting(true)
        setDnsResult(null)

        try {
            const data = await newHttpService.post('/api/backoffice/onboarding/dns-record', { company_name: companyName })
            setDnsResult({
                success: true,
                message: 'DNS record created successfully',
                detail: data.record,
            })
        } catch (err: any) {
            let errorMsg = 'Failed to create DNS record'
            try {
                if (err?.json) {
                    const body = await err.json()
                    errorMsg = body.error || errorMsg
                }
            } catch { /* ignore parse errors */ }
            setDnsResult({ success: false, message: errorMsg })
        } finally {
            setIsDnsSubmitting(false)
        }
    }

    const handleProvisionCompany = async () => {
        if (!isValidName || isProvisionSubmitting) return
        setIsProvisionSubmitting(true)
        setProvisionResult(null)

        try {
            const data = await newHttpService.post('/api/backoffice/onboarding/provision-company', { company_name: companyName })
            if (data.status === 'running') {
                setProvisionResult({
                    success: true,
                    message: data.message,
                    hint: 'The Lambda is still running. Check CloudWatch logs for completion.',
                })
            } else {
                setProvisionResult({
                    success: true,
                    message: data.message || 'AWS resources provisioned successfully',
                })
            }
        } catch (err: any) {
            let errorMsg = 'Failed to provision company'
            let retryable = false
            let hint: string | undefined
            try {
                if (err?.json) {
                    const body = await err.json()
                    errorMsg = body.error || errorMsg
                    retryable = body.retryable || false
                    hint = body.hint
                }
            } catch { /* ignore parse errors */ }
            setProvisionResult({ success: false, message: errorMsg, retryable, hint })
        } finally {
            setIsProvisionSubmitting(false)
        }
    }

    const handleVerifyOnboarding = async () => {
        if (!isValidName || isVerifySubmitting) return
        setIsVerifySubmitting(true)
        setVerifyResources(null)
        setVerifyError(null)

        try {
            const data = await newHttpService.post('/api/backoffice/onboarding/verify-provision', { company_name: companyName })
            if (data.resources) {
                setVerifyResources(data.resources)
            } else if (data.status === 'running') {
                setVerifyError('Verification is still running. Try again in a moment.')
            }
        } catch (err: any) {
            let errorMsg = 'Failed to verify onboarding'
            try {
                if (err?.json) {
                    const body = await err.json()
                    errorMsg = body.error || errorMsg
                }
            } catch { /* ignore parse errors */ }
            setVerifyError(errorMsg)
        } finally {
            setIsVerifySubmitting(false)
        }
    }



    const handleCreateAuditYear = async () => {
        if (!isValidName || !selectedAuditorId || isAuditYearSubmitting) return
        setIsAuditYearSubmitting(true)
        setAuditYearResult(null)

        try {
            const data = await newHttpService.post('/api/backoffice/onboarding/create-audit-year', {
                company_name: companyName,
                auditor_id: selectedAuditorId,
            })
            const entitySummary = data.entities.map((e: any) => `${e.entity_name} (${e.action})`).join(', ')
            setAuditYearResult({
                success: true,
                message: `Audit year ${data.year} created`,
                detail: entitySummary,
            })
        } catch (err: any) {
            let errorMsg = 'Failed to create audit year'
            try {
                if (err?.json) {
                    const body = await err.json()
                    errorMsg = body.error || errorMsg
                }
            } catch { /* ignore parse errors */ }
            setAuditYearResult({ success: false, message: errorMsg })
        } finally {
            setIsAuditYearSubmitting(false)
        }
    }

    const handleCreateEntity = async () => {
        if (!isValidName || !entityName.trim() || !entityAbbreviation.trim() || !createdCompanyId || isEntitySubmitting) return
        setIsEntitySubmitting(true)
        setEntityResult(null)

        try {
            const domains = domainInput.trim() || ''
            const query = createEntityMutation(createdCompanyId, entityName.trim(), entityAbbreviation.trim(), domains)
            const res = await newHttpService.graphql(isSilo ? companyName : 'auditech', query)
            if (!res?.createEntity?.entity) throw new Error('Create failed')
            const newEntity = res.createEntity.entity
            setCreatedEntities(prev => [...prev, { id: newEntity.id, name: newEntity.name }])
            setEntityResult({ success: true, message: `Entity "${newEntity.name}" created` })
            setEntityName('')
            setEntityAbbreviation('')
        } catch (err: any) {
            let errorMsg = 'Failed to create entity'
            try {
                if (err?.json) {
                    const body = await err.json()
                    errorMsg = body.error || errorMsg
                }
            } catch { /* ignore parse errors */ }
            setEntityResult({ success: false, message: errorMsg })
        } finally {
            setIsEntitySubmitting(false)
        }
    }

    const handleCreateEntityContext = async () => {
        if (!isValidName || !domainInput.trim() || isDomainSubmitting) return
        setIsDomainSubmitting(true)
        setDomainResult(null)

        const domains = domainInput.split(',').map(d => d.trim().toLowerCase()).filter(Boolean)
        if (!domains.length) {
            setDomainResult({ success: false, message: 'Enter at least one domain' })
            setIsDomainSubmitting(false)
            return
        }

        try {
            const data = await newHttpService.post('/api/backoffice/onboarding/entity-context-domains', {
                company_name: companyName,
                domains,
            })
            const entityNames = data.entities.map((e: any) => `${e.entity_name} (${e.action})`).join(', ')
            setDomainResult({
                success: true,
                message: `Entity context updated for: ${entityNames}`,
                detail: `Domains: ${domains.join(', ')}`,
            })
        } catch (err: any) {
            let errorMsg = 'Failed to create entity context'
            try {
                if (err?.json) {
                    const body = await err.json()
                    errorMsg = body.error || errorMsg
                }
            } catch { /* ignore parse errors */ }
            setDomainResult({ success: false, message: errorMsg })
        } finally {
            setIsDomainSubmitting(false)
        }
    }

    const setLogo = (file: File | null) => {
        setLogoFile(file)
        setLogoResult(null)
        if (file) {
            const reader = new FileReader()
            reader.onload = () => setLogoPreview(reader.result as string)
            reader.readAsDataURL(file)
        } else {
            setLogoPreview(null)
        }
    }

    const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file && file.type !== 'image/png') {
            setLogoResult({ success: false, message: 'Only PNG files are allowed' })
            e.target.value = ''
            return
        }
        if (file && file.size > 5 * 1024 * 1024) {
            setLogoResult({ success: false, message: 'File too large. Maximum is 5 MB' })
            e.target.value = ''
            return
        }
        setLogo(file || null)
    }

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        dragCounter.current++
        setIsDragging(true)
    }

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        dragCounter.current--
        if (dragCounter.current === 0) {
            setIsDragging(false)
        }
    }

    const handleLogoDrop = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        dragCounter.current = 0
        setIsDragging(false)
        const file = e.dataTransfer.files?.[0]
        if (!file) return
        if (file.type !== 'image/png') {
            setLogoResult({ success: false, message: 'Only PNG files are allowed' })
            return
        }
        if (file.size > 5 * 1024 * 1024) {
            setLogoResult({ success: false, message: 'File too large. Maximum is 5 MB' })
            return
        }
        setLogo(file)
    }

    const handleSetupAdminUsers = async () => {
        if (!isValidName || isAdminSubmitting) return
        setIsAdminSubmitting(true)
        setAdminResult(null)

        try {
            const validUsers = adminUsers.filter(u => u.email.trim())
            if (!validUsers.length) {
                setAdminResult({ success: false, message: 'Add at least one admin user' })
                setIsAdminSubmitting(false)
                return
            }
            const data = await newHttpService.post('/api/backoffice/onboarding/setup-admin-users', {
                company_name: companyName,
                admin_users: validUsers.map(u => ({
                    email: u.email.trim(),
                    first_name: u.firstName.trim(),
                    last_name: u.lastName.trim(),
                })),
            })
            const parts: string[] = []
            if (data.roles_created?.length) {
                parts.push(`Roles created: ${data.roles_created.join(', ')}`)
            }
            if (data.users_created?.length) {
                parts.push(`Users added: ${data.users_created.join(', ')}`)
            }
            if (!data.roles_created?.length && !data.users_created?.length) {
                parts.push('All roles and admin users already exist')
            }
            setAdminResult({
                success: true,
                message: parts.join('. '),
                detail: data.is_silo ? 'Silo roles were checked' : 'Non-silo — role insert skipped',
            })
            setAdminAuditSnippet({
                actor: actorLabel(currentUser),
                time: utilService.formatDateTime(new Date().toISOString()) || '—',
            })
            fetchUsersForCompany()
        } catch (err: any) {
            let errorMsg = 'Failed to setup admin users'
            try {
                if (err?.json) {
                    const body = await err.json()
                    errorMsg = body.error || errorMsg
                }
            } catch { /* ignore parse errors */ }
            setAdminResult({ success: false, message: errorMsg })
        } finally {
            setIsAdminSubmitting(false)
        }
    }

    const handleUploadLogo = async () => {
        if (!isValidName || !logoFile || isLogoSubmitting) return
        setIsLogoSubmitting(true)
        setLogoResult(null)

        try {
            const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = () => {
                    const result = reader.result as string
                    resolve(result.split(',')[1])
                }
                reader.onerror = reject
                reader.readAsDataURL(logoFile)
            })

            const data = await newHttpService.post('/api/backoffice/onboarding/upload-logo', {
                company_name: companyName,
                file_base64: base64,
            })
            setLogoResult({
                success: true,
                message: `Logo uploaded to ${data.bucket}`,
                detail: data.key,
            })
        } catch (err: any) {
            let errorMsg = 'Failed to upload logo'
            try {
                if (err?.json) {
                    const body = await err.json()
                    errorMsg = body.error || errorMsg
                }
            } catch { /* ignore parse errors */ }
            setLogoResult({ success: false, message: errorMsg })
        } finally {
            setIsLogoSubmitting(false)
        }
    }

    const handleCheckUser = async () => {
        if (!isValidName || !userEmail || isCheckingUser) return
        setIsCheckingUser(true)
        setUserCheckResult(null)
        setUserResult(null)
        try {
            const res = await newHttpService.get('/api/backoffice/onboarding/check-cognito-user', { email: userEmail, company_name: companyName })
            setUserCheckResult(res)
        } catch {
            setUserCheckResult(null)
        } finally {
            setIsCheckingUser(false)
        }
    }

    const handleCreateUser = async () => {
        if (!isValidName || !userEmail || isUserSubmitting) return
        setIsUserSubmitting(true)
        setUserResult(null)

        const userExists = userCheckResult?.exists === true

        try {
            if (userExists) {
                // User already exists — just add to group, no password needed
                const res = await newHttpService.post('/api/backoffice/onboarding/create-cognito-user', {
                    company_name: companyName,
                    email: userEmail,
                    password: '',
                })
                setUserResult({ success: true, message: res.message })
            } else {
                const actualPassword = userPassword || suggestedPassword
                const res = await newHttpService.post('/api/backoffice/onboarding/create-cognito-user', {
                    company_name: companyName,
                    email: userEmail,
                    password: actualPassword,
                })
                if (!res.already_exists) {
                    setCreatedUsers(prev => [...prev, { email: userEmail, password: actualPassword }])
                }
                setUserResult({ success: true, message: res.message || `User ${userEmail} created and assigned to ${companyName} group` })
            }
            fetchUsersForCompany()
            setUserEmail('')
            setUserPassword('')
            setUserCheckResult(null)
            setSuggestedPassword(generatePassword())
        } catch (err: any) {
            let errorMsg = 'Failed to create user'
            try {
                if (err?.json) {
                    const body = await err.json()
                    errorMsg = body.error || errorMsg
                }
            } catch { /* ignore parse errors */ }
            setUserResult({ success: false, message: errorMsg })
        } finally {
            setIsUserSubmitting(false)
        }
    }

    const handleCreateCompany = async () => {
        if (!isValidName || !abbreviation || isCompanySubmitting) return
        setIsCompanySubmitting(true)
        setCompanyResult(null)

        try {
            const data = await newHttpService.post('/api/backoffice/onboarding/create-company', {
                company_name: companyName,
                display_name: displayName,
                abbreviation,
                is_silo: isSilo,
            })
            setCreatedCompanyId(data.company_id)
            setCompanyResult({
                success: true,
                message: `Company created (ID: ${data.company_id})`,
                detail: `Secret: ${data.secret_name}`,
            })
        } catch (err: any) {
            let errorMsg = 'Failed to create company'
            try {
                if (err?.json) {
                    const body = await err.json()
                    errorMsg = body.error || errorMsg
                }
            } catch { /* ignore parse errors */ }

            // If company already exists, treat as success so onboarding can continue
            const alreadyExistsMatch = errorMsg.match(/already exists.*id=(\d+)/i)
            if (alreadyExistsMatch) {
                const existingId = Number(alreadyExistsMatch[1])
                setCreatedCompanyId(existingId)
                setCompanyResult({
                    success: true,
                    message: `Company "${companyName}" already exists (ID: ${existingId})`,
                    hint: 'Continuing with the existing company.',
                })
            } else {
                setCompanyResult({ success: false, message: errorMsg })
            }
        } finally {
            setIsCompanySubmitting(false)
        }
    }

    // Completed steps logic (0-5)
    const completedSteps = new Set<number>()
    if (companyResult?.success) completedSteps.add(0)
    if (dnsResult?.success) completedSteps.add(1)
    const awsResources = verifyResources?.filter(r => r.name !== 'Company Logo') || []
    const awsDone = !!(provisionResult?.success || (awsResources.length > 0 && awsResources.every(r => r.status === 'exists')))
    const logoExists = !!(logoResult?.success || (verifyResources && verifyResources.find(r => r.name === 'Company Logo')?.status === 'exists'))
    if (awsDone && logoExists) completedSteps.add(2)
    if (createdEntities.length > 0 && domainResult?.success && auditYearResult?.success) completedSteps.add(3)
    const hasCognitoUsers = createdUsers.length > 0 || cognitoGroupUsers.length > 0
    const hasDbAdminUsers = adminResult?.success || existingAdminUsers.length > 0
    if (hasCognitoUsers && hasDbAdminUsers) completedSteps.add(4)
    if (completedSteps.has(0) && completedSteps.has(1) && completedSteps.has(2) && completedSteps.has(3) && completedSteps.has(4)) completedSteps.add(5)

    // Review items for Step 5
    const reviewItems = [
        { label: 'Company', done: !!companyResult?.success },
        { label: 'DNS', done: !!dnsResult?.success },
        { label: 'AWS Resources', done: awsDone },
        { label: 'Logo', done: logoExists },
        { label: 'Entities', done: createdEntities.length > 0 },
        { label: 'Entity Context', done: !!domainResult?.success },
        { label: 'Audit Year', done: !!auditYearResult?.success },
        { label: 'Cognito Users', done: hasCognitoUsers },
        { label: 'AudiTech Platform Admin Users', done: hasDbAdminUsers },
        ...(isSilo ? [{ label: 'Health Report (Silo)', done: healthReportChecked }] : []),
    ]

    return (
        <section className="onboarding-page page-content-padding">
            <div className="onboarding-layout">
                {/* Step sidebar */}
                <aside className="onboarding-steps">
                    <h2 className="onboarding-steps-title">Company Onboarding</h2>
                    <ul className="step-list">
                        {STEPS.map((step, idx) => (
                            <li
                                key={step.key}
                                className={`step-item ${idx === activeStep ? 'active' : ''} ${completedSteps.has(idx) ? 'completed' : ''}`}
                                onClick={() => setActiveStep(idx)}
                            >
                                <span className="step-number">{completedSteps.has(idx) ? '\u2713' : idx + 1}</span>
                                <div className="step-info">
                                    <span className="step-label">{step.label}</span>
                                    <span className="step-desc">{step.description}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </aside>

                {/* Main content */}
                <div className="onboarding-content">
                    {/* Company name input — shared across steps */}
                    <div className="step-card company-name-card">
                        <div className="step-card-body">
                            <div className="company-name-form">
                                <label htmlFor="company-name">Company Name</label>
                                <div className="dns-input-row">
                                    <input
                                        id="company-name"
                                        type="text"
                                        value={companyName}
                                        onChange={handleCompanyNameChange}
                                        placeholder="e.g. acme"
                                        autoComplete="off"
                                    />
                                    <span className="dns-suffix">{DNS_SUFFIX}</span>
                                </div>
                                {companyName && !isValidName && (
                                    <span className="dns-validation-error">
                                        Lowercase letters, numbers, and hyphens only. Cannot start/end with hyphen.
                                    </span>
                                )}
                                {isLoadingState && (
                                    <div className="onboarding-auto-check">
                                        <div className="auto-check-spinner" />
                                        <span>Checking existing onboarding state...</span>
                                    </div>
                                )}
                                {!isLoadingState && companyResult?.success && companyResult?.hint === 'auto-loaded' && (
                                    <span className="onboarding-success-hint">&#10003; Existing company found — all checks complete</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Step 0: Company Setup */}
                    {activeStep === 0 && (
                        <div className="step-card">
                            <div className="step-card-header">
                                <h3>1. Company Setup</h3>
                                <p className="step-subtitle">
                                    Add the company to the <strong>company</strong> table and link the secret in <strong>company_secret</strong>.
                                </p>
                            </div>

                            <div className="step-card-body">
                                <div className="create-company-form">
                                    <div className="create-company-field">
                                        <label>Display Name</label>
                                        <span className="onboarding-field-hint" style={{ marginBottom: '0.25rem' }}>
                                            How the company name appears in the UI. Auto-formatted to Title Case (e.g., nano-dimension → Nano Dimension).
                                        </span>
                                        <div className="display-name-row">
                                            {useCustomName ? (
                                                <input
                                                    type="text"
                                                    value={customDisplayName}
                                                    onChange={e => setCustomDisplayName(e.target.value)}
                                                    placeholder="e.g. DDH, ICL"
                                                    autoComplete="off"
                                                />
                                            ) : (
                                                <span className="display-name-preview">{autoDisplayName || '<company>'}</span>
                                            )}
                                            <div>
                                                <label className="onboarding-checkbox-label" style={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={useCustomName}
                                                        onChange={e => {
                                                            setUseCustomName(e.target.checked)
                                                            if (e.target.checked) setCustomDisplayName(autoDisplayName)
                                                        }}
                                                    />
                                                    Custom name
                                                </label>
                                                <span className="onboarding-field-hint">
                                                    Override the auto-formatted name. Use for acronyms (DDH, ICL) or mixed casing (McAfee, SentinelOne).
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="create-company-field">
                                        <label htmlFor="abbreviation">Abbreviation</label>
                                        <input
                                            id="abbreviation"
                                            type="text"
                                            value={abbreviation}
                                            onChange={e => { setAbbreviation(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '')); setCompanyResult(null) }}
                                            placeholder="e.g. ACM"
                                            autoComplete="off"
                                        />
                                    </div>

                                    <label className="silo-toggle" onClick={() => setIsSilo(v => !v)}>
                                        <span className={`toggle-switch ${isSilo ? 'active' : ''}`}>
                                            <span className="toggle-switch-knob" />
                                        </span>
                                        <span className="silo-toggle-text">Silo database</span>
                                    </label>

                                    <div className="create-company-preview">
                                        <span className="create-company-preview-label">Will Create</span>
                                        <div className="create-company-preview-items">
                                            <div className="preview-item">
                                                <span className="preview-item-table">company</span>
                                                <span className="preview-item-value">
                                                    name: {displayName || '<company>'}, abbreviation: {abbreviation || '<abbr>'}, is_silo: {String(isSilo)}
                                                </span>
                                            </div>
                                            <div className="preview-item">
                                                <span className="preview-item-table">company_secret</span>
                                                <span className="preview-item-value">
                                                    secret_name: {companyName || '<company>'}-tokens
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        className="dns-submit-btn"
                                        onClick={handleCreateCompany}
                                        disabled={!isValidName || !abbreviation || isCompanySubmitting || companyResult?.success}
                                    >
                                        {isCompanySubmitting ? 'Creating...' : 'Create Company'}
                                    </button>

                                    {companyResult && <StepResultBanner result={companyResult} />}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 1: Domain Setup */}
                    {activeStep === 1 && (
                        <div className="step-card">
                            <div className="step-card-header">
                                <h3>2. Domain Setup</h3>
                                <p className="step-subtitle">
                                    Create a CNAME record in GoDaddy for the customer portal domain.
                                </p>
                            </div>

                            <div className="step-card-body">
                                <div className="dns-form">
                                    <div className="dns-preview" style={{ marginBottom: '0.75rem' }}>
                                        <span className="dns-preview-label">Record to Create</span>
                                        <div className="dns-preview-row">
                                            <span className="dns-preview-type">CNAME</span>
                                            {companyName ? (
                                                <a className="dns-preview-name" href={`https://${companyName}${DNS_SUFFIX}`} target="_blank" rel="noopener noreferrer">
                                                    {companyName}{DNS_SUFFIX}
                                                </a>
                                            ) : (
                                                <span className="dns-preview-name">{'<company>'}{DNS_SUFFIX}</span>
                                            )}
                                        </div>
                                        <div className="dns-preview-target">
                                            <span className="dns-preview-arrow">&rarr;</span>
                                            <span className="dns-preview-name">Customer Portal CloudFront</span>
                                        </div>
                                    </div>

                                    <div className="onboarding-instructions">
                                        <strong>Steps:</strong>
                                        <ol>
                                            <li>Go to <a href="https://dcc.godaddy.com" target="_blank" rel="noopener noreferrer">GoDaddy DNS Management</a></li>
                                            <li>Select domain <strong>{COMPANY_DOMAIN}</strong></li>
                                            <li>Click <strong>Add Record</strong></li>
                                            <li>Type: <strong>CNAME</strong>, Name: <strong>{companyName || '<company>'}{IS_US ? '.us' : ''}</strong>, Value: <strong>CloudFront distribution domain</strong>, TTL: <strong>1 Hour</strong></li>
                                            <li>Save, then click <strong>Check Domain</strong> below to verify</li>
                                        </ol>
                                    </div>

                                    <div className="provision-buttons">
                                        <button
                                            className="dns-submit-btn"
                                            disabled={!isValidName || dnsResult?.success || isDnsSubmitting}
                                            onClick={async () => {
                                                setIsDnsSubmitting(true)
                                                setDnsResult(null)
                                                try {
                                                    const checkData = await newHttpService.post('/api/backoffice/onboarding/check-domain', { company_name: companyName })
                                                    if (checkData.reachable) {
                                                        setDnsResult({ success: true, message: `Domain is reachable: ${checkData.url}` })
                                                    } else {
                                                        setDnsResult({ success: false, message: `Domain ${checkData.domain} is not reachable yet. DNS may still be propagating — try again in a few minutes.` })
                                                    }
                                                } catch {
                                                    setDnsResult({ success: false, message: 'Failed to check domain. Try again.' })
                                                } finally {
                                                    setIsDnsSubmitting(false)
                                                }
                                            }}
                                        >
                                            {isDnsSubmitting ? 'Checking...' : dnsResult?.success ? 'Domain Verified' : 'Check Domain'}
                                        </button>
                                    </div>

                                    {dnsResult && <StepResultBanner result={dnsResult} />}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: AWS Setup */}
                    {activeStep === 2 && (
                        <div className="step-card">
                            <div className="step-card-header">
                                <h3>3. AWS Setup</h3>
                                <p className="step-subtitle">
                                    Provision AWS resources and upload the company logo.
                                </p>
                            </div>

                            <div className="step-card-body">
                                {/* Sub-section: AWS Setup */}
                                <div className="step-subsection">
                                    <h4>AWS Setup</h4>
                                    <div className="provision-form">
                                        <div className="provision-resources">
                                            <span className="provision-resources-label">Resources Created</span>
                                            <ul className="provision-resources-list">
                                                <li>S3 bucket and policies</li>
                                                <li>KMS encryption keys</li>
                                                <li>API keys</li>
                                                <li>Cognito role</li>
                                            </ul>
                                        </div>

                                        <div className="provision-warning">
                                            <span className="provision-warning-icon">&#9888;</span>
                                            <span className="provision-warning-text">
                                                First run may fail because the new IAM role takes ~5 minutes to propagate. If it fails, wait 5 minutes and click again.
                                            </span>
                                        </div>

                                        <button
                                            className="dns-submit-btn"
                                            onClick={handleProvisionCompany}
                                            disabled={!isValidName || isProvisionSubmitting || provisionResult?.success}
                                        >
                                            {isProvisionSubmitting ? 'Setting Up...' : provisionResult?.success ? 'Already Provisioned' : 'Run Setup'}
                                        </button>

                                        {provisionResult && <StepResultBanner result={provisionResult} />}
                                    </div>
                                </div>

                                {/* Sub-section: Company Logo */}
                                <div className="step-subsection">
                                    <h4>Company Logo</h4>
                                    <div className="create-company-form">
                                        {!isBucketReady && (
                                            <div className="provision-warning">
                                                <span className="provision-warning-icon">&#9432;</span>
                                                <span className="provision-warning-text">
                                                    The S3 bucket has not been provisioned yet. Complete AWS Setup above before uploading the logo.
                                                </span>
                                            </div>
                                        )}

                                        <div className="provision-warning">
                                            <span className="provision-warning-icon">&#9432;</span>
                                            <span className="provision-warning-text">
                                                The logo must have a transparent background. If it doesn't, use <a href="https://www5.lunapic.com/editor/" target="_blank" rel="noopener noreferrer">LunaPic</a> to remove the background before uploading.
                                            </span>
                                        </div>

                                        <div
                                            className={`logo-dropzone ${isDragging ? 'dragging' : ''} ${logoPreview ? 'has-file' : ''}`}
                                            onDragEnter={handleDragEnter}
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={handleLogoDrop}
                                            onClick={() => document.getElementById('logo-file')?.click()}
                                        >
                                            <input
                                                id="logo-file"
                                                type="file"
                                                accept="image/png"
                                                onChange={handleLogoFileChange}
                                                style={{ display: 'none' }}
                                            />
                                            {logoPreview ? (
                                                <div className="logo-preview">
                                                    <div className="logo-preview-img-wrapper">
                                                        <img src={logoPreview} alt="Logo preview" className="logo-preview-img" />
                                                    </div>
                                                    <span className="logo-dropzone-filename">{logoFile?.name}</span>
                                                    <span className="logo-dropzone-hint">Click or drag to replace</span>
                                                </div>
                                            ) : (
                                                <div className="logo-dropzone-empty">
                                                    <span className="logo-dropzone-icon">&#128444;</span>
                                                    <span className="logo-dropzone-text">Drag & drop a PNG file here</span>
                                                    <span className="logo-dropzone-hint">or click to browse</span>
                                                </div>
                                            )}
                                        </div>

                                        <button
                                            className="dns-submit-btn"
                                            onClick={handleUploadLogo}
                                            disabled={!isValidName || !logoFile || isLogoSubmitting || !isBucketReady}
                                        >
                                            {isLogoSubmitting ? 'Uploading...' : 'Upload Logo'}
                                        </button>

                                        {logoResult && <StepResultBanner result={logoResult} />}
                                    </div>
                                </div>

                                {/* Sub-section: Verify */}
                                <div className="verify-section">
                                    <div className="verify-section-header">
                                        <span className="verify-section-icon">&#128269;</span>
                                        <div>
                                            <h4>Verify Resources</h4>
                                            <p>Run a check to confirm all AWS resources were created successfully.</p>
                                        </div>
                                    </div>

                                    <button
                                        className="dns-submit-btn"
                                        onClick={handleVerifyOnboarding}
                                        disabled={!isValidName || isVerifySubmitting}
                                        style={{ width: '100%' }}
                                    >
                                        {isVerifySubmitting ? 'Verifying...' : 'Run Verification'}
                                    </button>

                                    {verifyResources && (
                                        <div className={`verify-checklist ${verifyResources.every(r => r.status === 'exists') ? 'all-pass' : 'has-missing'}`} style={{ marginTop: '0.75rem' }}>
                                            <div className="verify-header">
                                                <span className="verify-header-icon">
                                                    {verifyResources.every(r => r.status === 'exists') ? '\u2713' : '\u26A0'}
                                                </span>
                                                <span className="verify-header-text">
                                                    {verifyResources.every(r => r.status === 'exists')
                                                        ? 'All resources verified'
                                                        : `${verifyResources.filter(r => r.status !== 'exists').length} of ${verifyResources.length} resources missing`}
                                                </span>
                                            </div>
                                            <div className="verify-grid">
                                                {verifyResources.map(r => (
                                                    <div key={r.name} className={`verify-row ${r.status === 'exists' ? 'exists' : 'missing'}`}>
                                                        <span className="verify-icon">{r.status === 'exists' ? '\u2713' : '\u2717'}</span>
                                                        <span className="verify-name">{r.name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {verifyError && <StepResultBanner result={{ success: false, message: verifyError }} />}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Entities Setup */}
                    {activeStep === 3 && (
                        <div className="step-card">
                            <div className="step-card-header">
                                <h3>4. Entities Setup</h3>
                                <p className="step-subtitle">
                                    Create entities, configure their context domains, and set up the initial audit year. All are required before running the pipeline.
                                </p>
                            </div>

                            <div className="step-card-body">
                                <div className="provision-form">
                                    <div className="step-subsection">
                                        <h4>Create Entities</h4>
                                        <p className="step-subsection-desc">
                                            Add the company's entities (e.g., subsidiaries, business units). Each entity needs a name and abbreviation.
                                        </p>

                                        {!createdCompanyId && (
                                            <div className="provision-warning">
                                                <span className="provision-warning-icon">&#9432;</span>
                                                <span className="provision-warning-text">
                                                    Create the company first (Step 1) before adding entities.
                                                </span>
                                            </div>
                                        )}

                                        {createdCompanyId && (
                                            <>
                                                <div className="entity-create-row">
                                                    <div className="entity-create-field">
                                                        <label htmlFor="entity-name-input">Entity Name</label>
                                                        <input
                                                            id="entity-name-input"
                                                            type="text"
                                                            value={entityName}
                                                            onChange={e => { setEntityName(e.target.value); setEntityResult(null) }}
                                                            placeholder="e.g. Main Entity"
                                                            autoComplete="off"
                                                        />
                                                    </div>
                                                    <div className="entity-create-field">
                                                        <label htmlFor="entity-abbr-input">Abbreviation</label>
                                                        <input
                                                            id="entity-abbr-input"
                                                            type="text"
                                                            value={entityAbbreviation}
                                                            onChange={e => { setEntityAbbreviation(e.target.value); setEntityResult(null) }}
                                                            placeholder="e.g. MAIN"
                                                            autoComplete="off"
                                                        />
                                                    </div>
                                                    <div className="entity-create-field entity-create-btn-wrapper">
                                                        <label>&nbsp;</label>
                                                        <button
                                                            className="dns-submit-btn"
                                                            onClick={handleCreateEntity}
                                                            disabled={!entityName.trim() || !entityAbbreviation.trim() || isEntitySubmitting}
                                                        >
                                                            {isEntitySubmitting ? 'Creating...' : 'Add Entity'}
                                                        </button>
                                                    </div>
                                                </div>

                                                {entityResult && <StepResultBanner result={entityResult} />}

                                                {createdEntities.length > 0 && (
                                                    <div className="entity-created-list">
                                                        <span className="entity-created-label">Created Entities:</span>
                                                        <ul>
                                                            {createdEntities.map(e => <li key={e.id}>{e.name} (ID: {e.id})</li>)}
                                                        </ul>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    <div className="step-subsection">
                                        <h4>Company Domains</h4>
                                        <p className="step-subsection-desc">
                                            Enter the company's email domain(s) to create entity_context records for all entities. This is required for the rule manager.
                                        </p>
                                        <div className="entity-create-field" style={{ marginBottom: '0.75rem' }}>
                                            <label htmlFor="domain-input">Domains</label>
                                            <input
                                                id="domain-input"
                                                type="text"
                                                value={domainInput}
                                                onChange={e => { setDomainInput(e.target.value); setDomainResult(null) }}
                                                placeholder="e.g. acme.com, acme.io"
                                                autoComplete="off"
                                            />
                                        </div>

                                        <button
                                            className="dns-submit-btn"
                                            onClick={handleCreateEntityContext}
                                            disabled={!isValidName || !domainInput.trim() || isDomainSubmitting || domainResult?.success}
                                        >
                                            {isDomainSubmitting ? 'Creating...' : domainResult?.success ? 'Entity Context Configured' : 'Create Entity Context'}
                                        </button>

                                        {domainResult && <StepResultBanner result={domainResult} />}
                                    </div>

                                    <div className="step-subsection">
                                        <h4>Audit Year</h4>
                                        <p className="step-subsection-desc">
                                            Creates an audit record for {new Date().getFullYear()} for each entity of the company.
                                        </p>

                                        <div className="entity-create-field" style={{ marginBottom: '0.75rem', maxWidth: '300px' }}>
                                            <label htmlFor="auditor-select">Auditor</label>
                                            <select
                                                id="auditor-select"
                                                className="auditor-select"
                                                value={selectedAuditorId ?? ''}
                                                onChange={e => setSelectedAuditorId(e.target.value ? Number(e.target.value) : null)}
                                            >
                                                <option value="">Select Auditor</option>
                                                {auditors.map(a => (
                                                    <option key={a.id} value={a.id}>{a.name}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <button
                                            className="dns-submit-btn"
                                            onClick={handleCreateAuditYear}
                                            disabled={!isValidName || !selectedAuditorId || isAuditYearSubmitting || auditYearResult?.success}
                                        >
                                            {isAuditYearSubmitting ? 'Creating...' : auditYearResult?.success ? `Audit Year ${new Date().getFullYear()} Exists` : `Create Audit Year ${new Date().getFullYear()}`}
                                        </button>

                                        {auditYearResult && <StepResultBanner result={auditYearResult} />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Users */}
                    {activeStep === 4 && (
                        <div className="step-card">
                            <div className="step-card-header">
                                <h3>5. Users</h3>
                                <p className="step-subtitle">
                                    Create Cognito users and set up admin team members.
                                </p>
                            </div>

                            <div className="step-card-body">
                                {/* Sub-section: Cognito Users */}
                                <div className="step-subsection">
                                    <h4>Cognito Users</h4>
                                    <div className="create-company-form">
                                        <div className="create-company-field">
                                            <label htmlFor="user-email">User Email</label>
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <input
                                                    id="user-email"
                                                    type="email"
                                                    value={userEmail}
                                                    onChange={e => { setUserEmail(e.target.value); setUserCheckResult(null); setUserResult(null) }}
                                                    placeholder="user@example.com"
                                                    autoComplete="off"
                                                    style={{ flex: 1 }}
                                                />
                                                <button
                                                    className="dns-submit-btn"
                                                    onClick={handleCheckUser}
                                                    disabled={!isValidName || !userEmail || isCheckingUser}
                                                    style={{ whiteSpace: 'nowrap' }}
                                                >
                                                    {isCheckingUser ? 'Checking...' : 'Check'}
                                                </button>
                                            </div>
                                        </div>

                                        {userCheckResult && (
                                            <div style={{ padding: '0.5rem 0.75rem', borderRadius: '4px', fontSize: '0.875rem', marginBottom: '0.5rem', background: userCheckResult.exists ? '#fff8e1' : '#e8f5e9', border: `1px solid ${userCheckResult.exists ? '#ffe082' : '#a5d6a7'}` }}>
                                                {userCheckResult.exists ? (
                                                    userCheckResult.in_group
                                                        ? `User already exists in Cognito and is in the ${companyName} group. Status: ${userCheckResult.status}`
                                                        : `User exists in Cognito (status: ${userCheckResult.status}) — click "Add to Group" to add them to ${companyName}.`
                                                ) : (
                                                    'User not found in Cognito — fill in a password and click "Create User".'
                                                )}
                                            </div>
                                        )}


                                        {(!userCheckResult || !userCheckResult.exists) && (
                                            <div className="create-company-field">
                                                <label htmlFor="user-password">Password</label>
                                                <input
                                                    id="user-password"
                                                    type="text"
                                                    value={userPassword}
                                                    onChange={e => { setUserPassword(e.target.value); setUserResult(null) }}
                                                    placeholder={suggestedPassword}
                                                    autoComplete="off"
                                                />
                                                <span className="onboarding-field-hint">
                                                    Leave empty to use the suggested password shown above
                                                </span>
                                            </div>
                                        )}

                                        {!userCheckResult?.in_group && (
                                            <button
                                                className="dns-submit-btn"
                                                onClick={handleCreateUser}
                                                disabled={!isValidName || !userEmail || isUserSubmitting || !userCheckResult}
                                            >
                                                {isUserSubmitting
                                                    ? (userCheckResult?.exists ? 'Adding...' : 'Creating...')
                                                    : (userCheckResult?.exists ? 'Add to Group' : 'Create User')}
                                            </button>
                                        )}

                                        {userResult && <StepResultBanner result={userResult} />}

                                        {createdUsers.length > 0 && (
                                            <div className="created-users-list">
                                                <span className="created-users-label">Created Users</span>
                                                {createdUsers.map((u, i) => {
                                                    const credText = `Hi,\n\nYour AudiTech portal account has been created.\n\nEmail: ${u.email}\nPassword: ${u.password}\n\nPlease log in and change your password.`
                                                    return (
                                                        <div key={i} className="created-user-card">
                                                            <div className="created-user-info">
                                                                <span className="created-user-email">{u.email}</span>
                                                                <span className="created-user-password">Password: {u.password}</span>
                                                            </div>
                                                            <button
                                                                className="copy-credentials-btn"
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(credText)
                                                                    const btn = document.getElementById(`copy-btn-${i}`)
                                                                    if (btn) { btn.textContent = 'Copied!'; setTimeout(() => { btn.textContent = 'Copy' }, 1500) }
                                                                }}
                                                                id={`copy-btn-${i}`}
                                                            >
                                                                Copy
                                                            </button>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        )}
                                    </div>
                                    {cognitoGroupUsers.length > 0 && (
                                        <div className="existing-users-table">
                                            <span className="created-users-label">Existing Cognito Group Members</span>
                                            <table className="onboarding-table">
                                                <thead>
                                                    <tr>
                                                        <th>Email</th>
                                                        <th>Status</th>
                                                        <th>Enabled</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {cognitoGroupUsers.map((u, i) => (
                                                        <tr key={i}>
                                                            <td>{u.email}</td>
                                                            <td>{u.status}</td>
                                                            <td>{u.enabled ? 'Yes' : 'No'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>

                                {/* Sub-section: AudiTech Platform Admin Users */}
                                <div className="step-subsection">
                                    <h4>AudiTech Platform Admin Users</h4>
                                    <div className="create-company-form">
                                        {isSilo && (
                                            <div className="provision-resources">
                                                <span className="provision-resources-label">Silo Roles</span>
                                                <ul className="provision-resources-list">
                                                    <li>overall-write</li>
                                                    <li>overall-read</li>
                                                    <li>ac-write</li>
                                                    <li>ac-read</li>
                                                    <li>control-owner</li>
                                                </ul>
                                            </div>
                                        )}

                                        <div className="admin-users-section">
                                            <div className="admin-users-grid">
                                                <span className="admin-grid-header">First Name</span>
                                                <span className="admin-grid-header">Last Name</span>
                                                <span className="admin-grid-header">Email</span>
                                                <span />
                                                {adminUsers.map((user, idx) => (
                                                    <React.Fragment key={idx}>
                                                        <input
                                                            type="text"
                                                            value={user.firstName}
                                                            onChange={e => {
                                                                const next = [...adminUsers]
                                                                next[idx] = { ...next[idx], firstName: e.target.value }
                                                                setAdminUsers(next)
                                                                setAdminResult(null)
                                                            }}
                                                            placeholder="First Name"
                                                        />
                                                        <input
                                                            type="text"
                                                            value={user.lastName}
                                                            onChange={e => {
                                                                const next = [...adminUsers]
                                                                next[idx] = { ...next[idx], lastName: e.target.value }
                                                                setAdminUsers(next)
                                                                setAdminResult(null)
                                                            }}
                                                            placeholder="Last Name"
                                                        />
                                                        <input
                                                            type="email"
                                                            value={user.email}
                                                            onChange={e => {
                                                                const next = [...adminUsers]
                                                                next[idx] = { ...next[idx], email: e.target.value }
                                                                setAdminUsers(next)
                                                                setAdminResult(null)
                                                            }}
                                                            placeholder="Email"
                                                        />
                                                        <button
                                                            className="admin-user-remove"
                                                            onClick={() => {
                                                                setAdminUsers(adminUsers.filter((_, i) => i !== idx))
                                                                setAdminResult(null)
                                                            }}
                                                            title="Remove"
                                                        >
                                                            &times;
                                                        </button>
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                            <button
                                                className="admin-user-add"
                                                onClick={() => {
                                                    setAdminUsers([...adminUsers, { email: '', firstName: '', lastName: '' }])
                                                    setAdminResult(null)
                                                }}
                                            >
                                                + Add User
                                            </button>
                                        </div>

                                        <button
                                            className="dns-submit-btn"
                                            onClick={handleSetupAdminUsers}
                                            disabled={!isValidName || isAdminSubmitting}
                                        >
                                            {isAdminSubmitting ? 'Setting up...' : 'Setup AudiTech Platform Admin Users'}
                                        </button>

                                        {adminResult && <StepResultBanner result={adminResult} />}
                                        {adminAuditSnippet && (
                                            <div className="audit-snippet">
                                                <strong>Last modified by {adminAuditSnippet.actor} at {adminAuditSnippet.time}</strong>
                                            </div>
                                        )}

                                        {existingAdminUsers.length > 0 && (
                                            <div className="existing-users-table">
                                                <span className="created-users-label">Existing AudiTech Platform Admin Users</span>
                                                <table className="onboarding-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Email</th>
                                                            <th>First Name</th>
                                                            <th>Last Name</th>
                                                            <th>Roles</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {existingAdminUsers.map((u, i) => (
                                                            <tr key={i}>
                                                                <td>{u.email}</td>
                                                                <td>{u.first_name}</td>
                                                                <td>{u.last_name}</td>
                                                                <td>{u.roles.join(', ') || '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 5: Review */}
                    {activeStep === 5 && (
                        <div className="step-card">
                            <div className="step-card-header">
                                <h3>6. Review</h3>
                                <p className="step-subtitle">
                                    Verify that all onboarding steps have been completed successfully.
                                </p>
                            </div>

                            <div className="step-card-body">
                                <div className="review-summary">
                                    {reviewItems.map(item => (
                                        <div key={item.label} className={`review-item ${item.done ? 'done' : 'pending'}`}>
                                            <span className="review-item-icon">{item.done ? '\u2713' : '\u2022'}</span>
                                            <span className="review-item-label">{item.label}</span>
                                            <span className="review-item-status">{item.done ? 'Complete' : 'Pending'}</span>
                                        </div>
                                    ))}
                                </div>

                                {isSilo && (
                                    <div className="step-subsection">
                                        <h4>Health Report (Silo Only)</h4>
                                        <p className="step-subsection-desc">
                                            Register this silo company in the daily health report.
                                        </p>
                                        <label className="onboarding-checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={healthReportChecked}
                                                onChange={e => setHealthReportChecked(e.target.checked)}
                                            />
                                            I have added <strong>{companyName || '<company>'}</strong> to generate_health_report in serverless-etl-integration-metadata
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}

const StepResultBanner: React.FC<{ result: StepResult }> = ({ result }) => (
    <div className={`dns-result ${result.success ? 'success' : 'error'}`}>
        <span className="dns-result-icon">{result.success ? '\u2713' : '\u2717'}</span>
        <div className="dns-result-text">
            <span className="dns-result-message">{result.message}</span>
            {result.detail && <span className="dns-result-record">{result.detail}</span>}
            {result.hint && <span className="dns-result-hint">{result.hint}</span>}
            {result.retryable && (
                <span className="dns-result-retry">This error is retryable — wait 5 minutes and try again.</span>
            )}
        </div>
    </div>
)
